import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getReporteVentas } from "../helpers/rutaVentas";
import logo from "../images/ridersmiami-logo.png";

const todayLocalISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();
  d.setDate(1);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return "";

  // Si viene de Mongo como ISO: 2026-06-01T00:00:00.000Z
  // tomamos la fecha textual sin moverla por zona horaria.
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (m) {
      return `${m[3]}/${m[2]}/${m[1]}`;
    }
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("es-AR");
};

const formatUSD = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const formatUSDPdf = (value) => {
  const n = Number(value || 0);

  return `US$${n.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatNum = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const nombreClienteCompra = (cliente) => {
  if (!cliente) return "";
  return (
    cliente.razonsocial ||
    [cliente.apellido1, cliente.apellido2, cliente.nombre]
      .filter(Boolean)
      .join(" ") ||
    ""
  );
};

const nombreClienteRecibe = (cliente) => {
  if (!cliente) return "";
  return (
    cliente.nombreCompleto ||
    [cliente.nombre, cliente.segundoNombre, cliente.apellido1, cliente.apellido2]
      .filter(Boolean)
      .join(" ") ||
    ""
  );
};

const nombreUsuario = (usuario) => {
  if (!usuario) return "";

  if (typeof usuario === "string") {
    return "";
  }

  return (
    [usuario.nombres, usuario.apellidos].filter(Boolean).join(" ") ||
    [usuario.nombre, usuario.apellido].filter(Boolean).join(" ") ||
    usuario.email ||
    ""
  );
};

const nombreVendedorVenta = (fila) => {
  return (
    fila?.vendedorNombre ||
    fila?.usuarioNombre ||
    nombreUsuario(fila?.vendedor) ||
    nombreUsuario(fila?.usuario) ||
    ""
  );
};

const normalizarTexto = (value) => String(value || "").toUpperCase();

export default function ReporteVentas() {
  const [desde, setDesde] = useState(firstDayMonthISO());
  const [hasta, setHasta] = useState(todayLocalISO());
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [estadoItem, setEstadoItem] = useState("");
  const [categoria, setCategoria] = useState("");
  const [vendedor, setVendedor] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filas, setFilas] = useState([]);
  const [totales, setTotales] = useState({
    cantidadVentas: 0,
    cantidadItems: 0,
    cantidadProductos: 0,
    totalProductosUSD: 0,
    totalEnvioUSD: 0,
    totalUSD: 0,
    totalComisionUSD: 0,
    itemsBajoMinimo: 0,
  });

  const cargarReporte = async () => {
    setLoading(true);
    setError("");

    try {
        const data = await getReporteVentas({
          desde,
          hasta,
          q,
          estado,
          estadoItem,
          categoria,
          vendedor,
        });

        console.log("DATA REPORTE VENTAS:", data);

        console.log(
        "VENDEDORES:",
        data?.filas?.map((f) => ({
            venta: f.nroventa,
            vendedorNombre: f.vendedorNombre,
            usuarioNombre: f.usuarioNombre,
            vendedor: f.vendedor,
            usuario: f.usuario,
        }))
        );

      setFilas(Array.isArray(data?.filas) ? data.filas : []);
      setTotales(data?.totales || {});
    } catch (err) {
      console.error("Error reporte ventas:", err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error al cargar el reporte"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoriasDisponibles = useMemo(() => {
    const set = new Set();

    filas.forEach((fila) => {
      if (fila.categoriaCodigo) {
        set.add(fila.categoriaCodigo);
      }
    });

    return Array.from(set).sort();
  }, [filas]);

  const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

  const vendedoresDisponibles = useMemo(() => {
    const map = new Map();

    filas.forEach((fila) => {
      const id = getId(fila.vendedor) || getId(fila.usuario);
      const nombre = nombreVendedorVenta(fila);

      if (id && nombre && !map.has(id)) {
        map.set(id, nombre);
      }
    });

    return Array.from(map.entries())
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [filas]);

  const limpiarFiltros = () => {
    setDesde(firstDayMonthISO());
    setHasta(todayLocalISO());
    setQ("");
    setEstado("");
    setEstadoItem("");
    setCategoria("");
    setVendedor("");
  };

const generarPDF = async () => {
  try {
    const doc = new jsPDF("landscape", "mm", "a4");
    const logoImg = await cargarImagen(logo);

    // ---------------- ENCABEZADO ----------------
    doc.addImage(logoImg, "PNG", 10, 8, 34, 12);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Reporte de Ventas", 50, 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Riders Miami", 50, 20);
    doc.text(`Desde: ${desde || "-"}   Hasta: ${hasta || "-"}`, 220, 14, {
      align: "right",
    });
    doc.text(
      `Estado venta: ${estado || "Todos"} | Estado artículo: ${
        estadoItem || "Todos"
      } | Vendedor: ${
        vendedoresDisponibles.find((v) => v.id === vendedor)?.nombre || "Todos"
      } | Categoría: ${categoria || "Todas"}`,
      287,
      20,
      { align: "right" }
    );

    doc.setDrawColor(180);
    doc.line(10, 24, 287, 24);

    // ---------------- RESUMEN / TOTALES ----------------
    const resumenY = 28;
    const boxW = 44;
    const boxH = 14;
    const gap = 3;

    const resumen = [
      { label: "Ventas", value: String(totales?.cantidadVentas || 0) },
      {
        label: "Productos",
        value: formatNum(totales?.cantidadProductos || 0),
      },
      {
        label: "Productos USD",
        value: formatUSD(totales?.totalProductosUSD || 0),
      },
      {
        label: "Envío USD",
        value: formatUSD(totales?.totalEnvioUSD || 0),
      },
      {
        label: "Total USD",
        value: formatUSD(totales?.totalUSD || 0),
      },
      {
        label: "Comisión USD",
        value: formatUSD(totales?.totalComisionUSD || 0),
      },
    ];

    resumen.forEach((item, i) => {
      const x = 10 + i * (boxW + gap);

      doc.setDrawColor(210);
      doc.roundedRect(x, resumenY, boxW, boxH, 1.5, 1.5);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(item.label, x + 2, resumenY + 4.5);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(String(item.value), x + 2, resumenY + 10);
    });

    // ---------------- TABLA ----------------
    const body = filas.map((fila) => [
      formatDate(fila.fecha),
      fila.nroventa || "",
      normalizarTexto(nombreClienteCompra(fila.clienteCompra)),
      normalizarTexto(nombreClienteRecibe(fila.clienteRecibe)),
      normalizarTexto(nombreVendedorVenta(fila)),
      fila.categoriaCodigo || fila.categoriaNombre || "",
      fila.marcaNombre || "",
      fila.modeloProducto || "",
      fila.descripcionProducto || "",
      formatNum(fila.cantidad),
      formatUSDPdf(fila.precioVentaUSD),
      formatUSDPdf(fila.subtotalEnvioUSD),
      formatUSDPdf(fila.subtotalUSD),
      formatUSDPdf(fila.comisionCalculadaUSD),
      fila.estadoVenta || "",
      fila.estadoItem || "",
      fila.vendidoBajoMinimo ? "SI" : "NO",
    ]);

    autoTable(doc, {
      startY: 48,
      theme: "grid",
      margin: { left: 10, right: 10 },
      head: [
        [
          "Fecha",
          "Venta",
          "Cliente compra",
          "Cliente recibe",
          "Vendedor",
          "Categoría",
          "Marca",
          "Modelo",
          "Producto",
          "Cant.",
          "Precio",
          "Envío",
          "Subtotal",
          "Comisión",
          "Estado",
          "Item",
          "Bajo mín.",
        ],
      ],
      body,
      foot: [
        [
          "Totales",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          formatNum(totales?.cantidadProductos || 0),
          "",
          formatUSDPdf(totales?.totalEnvioUSD || 0),
          formatUSDPdf(totales?.totalUSD || 0),
          formatUSDPdf(totales?.totalComisionUSD || 0),
          "",
          "",
          "",
        ],
      ],
      styles: {
        fontSize: 5.8,
        cellPadding: 0.9,
        overflow: "linebreak",
        valign: "middle",
        minCellHeight: 5,
      },
      headStyles: {
        fillColor: [33, 37, 41],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 5.8,
        halign: "center",
      },
      footStyles: {
        fillColor: [245, 245, 245],
        textColor: 20,
        fontStyle: "bold",
        fontSize: 5.8,
      },
      margin: { left: 6, right: 6 },
      columnStyles: {
        0: { cellWidth: 17 }, // Fecha
        1: { cellWidth: 8, halign: "center" }, // Venta

        2: { cellWidth: 31 }, // Cliente compra
        3: { cellWidth: 31 }, // Cliente recibe
        4: { cellWidth: 17 }, // Vendedor

        5: { cellWidth: 12 }, // Categoría
        6: { cellWidth: 10 }, // Marca
        7: { cellWidth: 17 }, // Modelo
        8: { cellWidth: 19 }, // Producto

        9: { cellWidth: 7, halign: "right" }, // Cant.

        10: { cellWidth: 19, halign: "right", overflow: "visible" }, // Precio
        11: { cellWidth: 17, halign: "right", overflow: "visible" }, // Envío
        12: { cellWidth: 20, halign: "right", overflow: "visible" }, // Subtotal
        13: { cellWidth: 17, halign: "right", overflow: "visible" }, // Comisión

        14: { cellWidth: 13 }, // Estado
        15: { cellWidth: 13 }, // Item
        16: { cellWidth: 8, halign: "center" }, // Bajo mín.
      },
      didDrawPage: function () {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(
          `Página ${pageCount}`,
          doc.internal.pageSize.getWidth() - 10,
          doc.internal.pageSize.getHeight() - 6,
          { align: "right" }
        );
      },
    });

    doc.save(`reporte-ventas-riders-${desde || "desde"}-${hasta || "hasta"}.pdf`);
  } catch (error) {
    console.error("Error generando PDF:", error);
    alert("No se pudo generar el PDF.");
  }
};

  return (
    <div className="container-fluid mt-3 mb-4">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-0">Reporte de Ventas</h4>
              <small className="text-muted">Riders Miami</small>
            </div>

            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={limpiarFiltros}>
                Limpiar
              </Button>

              <Button variant="dark" size="sm" onClick={cargarReporte} disabled={loading}>
                {loading ? "Buscando..." : "Buscar"}
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={generarPDF}
                disabled={loading || filas.length === 0}
              >
                PDF
              </Button>
            </div>
          </div>

        <Row className="g-2 mb-3 align-items-end">
          <Col xs={12} sm={6} lg>
            <Form.Label>Desde</Form.Label>
            <Form.Control
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              size="sm"
            />
          </Col>

          <Col xs={12} sm={6} lg>
            <Form.Label>Hasta</Form.Label>
            <Form.Control
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              size="sm"
            />
          </Col>

          <Col xs={12} sm={6} lg>
            <Form.Label>Estado venta</Form.Label>
            <Form.Select
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              size="sm"
            >
              <option value="">Todos</option>
              <option value="PENDIENTE">PENDIENTE</option>
              <option value="CONFIRMADA">CONFIRMADA</option>
              <option value="EN_PROCESO">EN PROCESO</option>
              <option value="ENVIADA">ENVIADA</option>
              <option value="ENTREGADA">ENTREGADA</option>
              <option value="ANULADA">ANULADA</option>
            </Form.Select>
          </Col>

          <Col xs={12} sm={6} lg>
            <Form.Label>Estado artículo</Form.Label>
            <Form.Select
              value={estadoItem}
              onChange={(e) => setEstadoItem(e.target.value)}
              size="sm"
            >
              <option value="">Todos</option>
              <option value="PENDIENTE">PENDIENTE</option>
              <option value="CONFIRMADO">CONFIRMADO</option>
              <option value="EN_PROCESO">EN PROCESO</option>
              <option value="ENVIADO">ENVIADO</option>
              <option value="ENTREGADO">ENTREGADO</option>
              <option value="ANULADO">ANULADO</option>
            </Form.Select>
          </Col>

          <Col xs={12} sm={6} lg>
            <Form.Label>Vendedor</Form.Label>
            <Form.Select
              value={vendedor}
              onChange={(e) => setVendedor(e.target.value)}
              size="sm"
            >
              <option value="">Todos</option>
              {vendedoresDisponibles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.nombre}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col xs={12} sm={6} lg>
            <Form.Label>Categoría</Form.Label>
            <Form.Select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              size="sm"
            >
              <option value="">Todas</option>
              {categoriasDisponibles.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col xs={12} sm={6} lg>
            <Form.Label>Buscar</Form.Label>
            <Form.Control
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Venta, producto..."
              size="sm"
            />
          </Col>
        </Row>

          {error && (
            <div className="alert alert-danger py-2">
              {error}
            </div>
          )}

          <Row className="g-2 mb-3">
            <Col Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Ventas</small>
                  <h5 className="mb-0">{totales?.cantidadVentas || 0}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Productos</small>
                  <h5 className="mb-0">{formatNum(totales?.cantidadProductos || 0)}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Productos USD</small>
                  <h5 className="mb-0">{formatUSD(totales?.totalProductosUSD || 0)}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Envío USD</small>
                  <h5 className="mb-0">{formatUSD(totales?.totalEnvioUSD || 0)}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Total USD</small>
                  <h5 className="mb-0">{formatUSD(totales?.totalUSD || 0)}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Comisión USD</small>
                  <h5 className="mb-0">{formatUSD(totales?.totalComisionUSD || 0)}</h5>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="table-responsive" style={{ maxHeight: "65vh" }}>
            <Table striped bordered hover size="sm" className="align-middle">
              <thead className="table-dark" style={{ position: "sticky", top: 0, zIndex: 2 }}>
                <tr>
                  <th>Fecha</th>
                  <th>Venta</th>
                  <th>Cliente compra</th>
                  <th>Cliente recibe</th>
                  <th>Vendedor</th>
                  <th>Categoría</th>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th>Producto</th>
                  <th className="text-end">Cant.</th>
                  <th className="text-end">Precio</th>
                  <th className="text-end">Envío</th>
                  <th className="text-end">Subtotal</th>
                  <th className="text-end">Comisión</th>
                  <th>Estado</th>
                  <th>Item</th>
                  <th>Bajo mín.</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="17" className="text-center py-4">
                      <Spinner animation="border" size="sm" /> Cargando reporte...
                    </td>
                  </tr>
                ) : filas.length === 0 ? (
                  <tr>
                    <td colSpan="17" className="text-center py-4 text-muted">
                      No hay ventas para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  filas.map((fila, index) => (
                    <tr key={`${fila.ventaId}-${fila.itemId}-${index}`}>
                      <td>{formatDate(fila.fecha)}</td>
                      <td>{fila.nroventa}</td>
                      <td>{normalizarTexto(nombreClienteCompra(fila.clienteCompra))}</td>
                      <td>{normalizarTexto(nombreClienteRecibe(fila.clienteRecibe))}</td>
                      <td>{normalizarTexto(nombreVendedorVenta(fila))}</td>
                      <td>{fila.categoriaCodigo || fila.categoriaNombre}</td>
                      <td>{fila.marcaNombre}</td>
                      <td>{fila.modeloProducto}</td>
                      <td>{fila.descripcionProducto}</td>
                      <td className="text-end">{formatNum(fila.cantidad)}</td>
                      <td className="text-end">{formatUSD(fila.precioVentaUSD)}</td>
                      <td className="text-end">{formatUSD(fila.subtotalEnvioUSD)}</td>
                      <td className="text-end fw-bold">{formatUSD(fila.subtotalUSD)}</td>
                      <td className="text-end">{formatUSD(fila.comisionCalculadaUSD)}</td>
                      <td>{fila.estadoVenta}</td>
                      <td>{fila.estadoItem}</td>
                      <td className={fila.vendidoBajoMinimo ? "text-danger fw-bold" : ""}>
                        {fila.vendidoBajoMinimo ? "SI" : "NO"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {filas.length > 0 && (
                <tfoot>
                  <tr className="reporte-ventas-total-row">
                    <th colSpan="9">Totales</th>
                    <th className="text-end">{formatNum(totales?.cantidadProductos || 0)}</th>
                    <th></th>
                    <th className="text-end">{formatUSD(totales?.totalEnvioUSD || 0)}</th>
                    <th className="text-end">{formatUSD(totales?.totalUSD || 0)}</th>
                    <th className="text-end">{formatUSD(totales?.totalComisionUSD || 0)}</th>
                    <th colSpan="3"></th>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}